import { faExternalLinkSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "~/lib/utils";
import { Anchor } from "~/components/Anchor";
import { ColumnMetadata, ColumnTypes } from "~/components/DataGrid";
import { Emdash } from "~/components/Punctuation";
import { AssetColumns } from "~/modules/assets/types";
import { parseCIK } from "~/utils/parseCIK";
import { AssetTableRowData } from "../types";
import { TooltipPassthru } from "../TooltipPassthru";

// // // //

function TreasuryIssuerWithCik(props: {
    item: AssetTableRowData;
    justify: "right" | "left";
}) {
    const { item, justify } = props;
    if (item.isAsset) {
        const issuer = item.issuer;
        if (!issuer) return <Emdash />;

        // Parse + format CIK id
        const cik = parseCIK(issuer.cik);

        // Just display issuer name if CIK is null
        if (cik === null) {
            return (
                <div className="flex items-center">
                    {issuer.legal_name || issuer.name}
                </div>
            );
        }

        // Display CIK value with link
        return (
            <div
                className={cn("flex flex-col justify-center w-full", {
                    "items-start": justify === "left",
                    "items-end": justify === "right",
                })}
            >
                <div className="dg-overflow">
                    {issuer.legal_name || issuer.name}
                </div>

                <TooltipPassthru tooltipContent="Click to view on SEC Edgar Search">
                    <Anchor
                        href={`https://www.sec.gov/edgar/search/#/entityName=${cik}`}
                        analyticsLabel="Asset Table - SEC Edgar CIK Lookup"
                        variant="default"
                        className="font-mono text-xs flex items-center gap-1"
                        openInNewTab
                    >
                        {cik}
                        <FontAwesomeIcon
                            icon={faExternalLinkSquare}
                            className="h-3"
                        />
                    </Anchor>
                </TooltipPassthru>
            </div>
        );
    }

    return <Emdash />;
}

export const TREASURY_ISSUER_WITH_CIK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "issuer_with_cik",
    label: (
        <>
            {/* Let's try this even though its inconsistent? */}
            <p>Issuer Legal Name</p>
            <p className="text-xs">SEC CIK Number</p>
        </>
    ),
    tooltip:
        "The Central Index Key (CIK) is used on the SEC's computer systems to identify corporations and individual people who have filed disclosure with the SEC.",
    type: ColumnTypes.string,
    sortsOn: AssetColumns.issuerName,
    width: 250,
    loginRequired: true,
    render: (item) => {
        return <TreasuryIssuerWithCik item={item} justify="left" />;
    },
};

// // // //

export const ISSUER_LEGAL_NAME: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "issuer_legal_name",
    label: "Issuer Legal Name",
    type: ColumnTypes.string,
    sortsOn: AssetColumns.issuerName,
    width: 220,
    loginRequired: true,
    render: (item) => {
        if (item.isAsset) {
            const issuer = item.issuer;
            if (!issuer)
                return <span className="text-muted-foreground">Private</span>;
            return issuer.legal_name || issuer.name;
        }

        return <Emdash />;
    },
};

// // // //

export const ISSUER_LEI: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    slug: "issuer_lei",
    label: "Issuer LEI",
    tooltip: "Legal Entity Name of the Issuer",
    type: ColumnTypes.number,
    width: 220,
    render: (item) => {
        if (item.isAsset) {
            const issuer = item.issuer;
            if (!issuer) return <Emdash />;
            if (!issuer.lei) return <Emdash />;

            const lei = issuer.lei;

            return (
                <TooltipPassthru tooltipContent="Click to view on LEI-Lookup">
                    <Anchor
                        href={`https://www.lei-lookup.com/record/${lei}`}
                        analyticsLabel="Asset Table - LEI Lookup Link"
                        variant="default"
                        className="font-mono text-xs flex items-center gap-1"
                        openInNewTab
                    >
                        {lei}
                        <FontAwesomeIcon
                            icon={faExternalLinkSquare}
                            className="h-3"
                        />
                    </Anchor>
                </TooltipPassthru>
            );
        }

        return <Emdash />;
    },
};

// // // //

export const ISSUER_CIK: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    slug: "Issuer CIK",
    label: "Issuer CIK",
    tooltip:
        "The Central Index Key (CIK) is used on the SEC's computer systems to identify corporations and individual people who have filed disclosure with the SEC.",
    type: ColumnTypes.number,
    width: 150,
    render: (item) => {
        if (item.isAsset) {
            const issuer = item.issuer;
            if (!issuer) return <Emdash />;

            // Parse + format CIK id
            const cik = parseCIK(issuer.cik);

            // Just display issuer name if CIK is null
            if (cik === null) {
                return <Emdash />;
            }

            return (
                <TooltipPassthru tooltipContent="Click to view on SEC Edgar Search">
                    <Anchor
                        href={`https://www.sec.gov/edgar/search/#/entityName=${cik}`}
                        analyticsLabel="Asset Table - SEC Edgar CIK Lookup"
                        variant="default"
                        className="font-mono text-xs flex items-center gap-1"
                        openInNewTab
                    >
                        {cik}
                        <FontAwesomeIcon
                            icon={faExternalLinkSquare}
                            className="h-3"
                        />
                    </Anchor>
                </TooltipPassthru>
            );
        }

        return <Emdash />;
    },
};
